import { type ComponentType, type ReactElement, useState } from 'react';
import type { AnyObject } from 'utils/types/AnyObject';

export type DialogHook = [
  dialogElement: ReactElement | null,
  openDialog: () => void,
  closeDialog: () => void,
];

interface Props<TComponentProps extends AnyObject> {
  component: ComponentType<TComponentProps & { onClose: () => void }>;
  dialogProps?: TComponentProps;
  initialOpen?: boolean;
}

interface UseDialog {
  <TComponentProps extends AnyObject>(props: {
    component: ComponentType<TComponentProps & { onClose: () => void }>;
    dialogProps: TComponentProps;
    initialOpen?: boolean;
  }): DialogHook;
  (props: {
    component: ComponentType<{ onClose: () => void }>;
    initialOpen?: boolean;
  }): DialogHook;
}

export const useDialog: UseDialog = <TComponentProps extends AnyObject>({
  component: Component,
  dialogProps,
  initialOpen = false,
}: Props<TComponentProps>): DialogHook => {
  const [open, setOpen] = useState(initialOpen);

  const openDialog = () => setOpen(true);
  const closeDialog = () => setOpen(false);

  const dialogElement = open ? (
    <Component
      {...(dialogProps as TComponentProps)}
      onClose={closeDialog}
      onSubmitSuccess={closeDialog}
    />
  ) : null;

  return [dialogElement, openDialog, closeDialog];
};
